/**
 * Trovva — CSV Import for Leads/Contacts
 *
 * Simplified idiot-proof flow:
 *   1. Upload a .csv file (drag or click)
 *   2. AI silently analyzes & maps columns + normalizes data
 *   3. Clean preview: "We found 47 contacts — here's a sample"
 *   4. One big Import button → done
 *
 * Column mapping is hidden — only appears if user clicks "Adjust columns"
 * or if AI can't figure out the required fields.
 */
import { useState, useMemo } from 'react';
import { Upload, X, CheckCircle2, AlertCircle, Loader2, Sparkles, Settings2, HelpCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { normalizeRows } from '../../lib/csvNormalizer';

const TROVVA_FIELDS = [
  { key: '',           label: '— ignore —' },
  { key: 'first_name', label: 'First name' },
  { key: 'last_name',  label: 'Last name' },
  { key: 'full_name',  label: 'Full name (auto-split)' },
  { key: 'email',      label: 'Email' },
  { key: 'phone',      label: 'Phone' },
  { key: 'phone_secondary', label: 'Phone 2' },
  { key: 'address',    label: 'Street address' },
  { key: 'city',       label: 'City' },
  { key: 'state',      label: 'State' },
  { key: 'zip',        label: 'ZIP' },
  { key: 'role',       label: 'Role' },
  { key: 'lead_type',  label: 'Lead type' },
  { key: 'lead_source',label: 'Lead source' },
  { key: 'budget',     label: 'Budget' },
  { key: 'timeline',   label: 'Timeline' },
  { key: 'notes',      label: 'Notes' },
  { key: 'tags',       label: 'Tags' },
];

// Which columns to show in the clean preview table
const PREVIEW_COLS = ['first_name', 'last_name', 'email', 'phone', 'address', 'lead_source'];
const PREVIEW_LABELS = { first_name: 'First', last_name: 'Last', email: 'Email', phone: 'Phone', address: 'Address', lead_source: 'Source' };

export default function CsvImport({ open, onClose, onImported, onNavigateTab }) {
  // Steps: 'upload' | 'analyzing' | 'needs_help' | 'preview' | 'importing' | 'done' | 'mapping'
  const [step, setStep] = useState('upload');
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [dedupe] = useState('email'); // Sensible default, no need to ask
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [aiMapping, setAiMapping] = useState(null);
  const [fileName, setFileName] = useState('');
  const [cameFromHelp, setCameFromHelp] = useState(false);

  const mappedRows = useMemo(() => {
    if (!rawRows.length) return [];
    if (aiMapping) return normalizeRows(rawRows, aiMapping, 'contacts');
    return rawRows.map(r => {
      const out = {};
      Object.entries(mapping).forEach(([idx, field]) => {
        if (field) out[field] = (r[idx] || '').trim();
      });
      return out;
    });
  }, [rawRows, mapping, aiMapping]);

  const hasRequired = useMemo(() => {
    const used = Object.values(mapping);
    return (used.includes('first_name') && used.includes('last_name')) || used.includes('full_name');
  }, [mapping]);

  // Count how many fields were successfully mapped
  const mappedCount = useMemo(() => Object.values(mapping).filter(Boolean).length, [mapping]);

  if (!open) return null;

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      const { headers, rows } = parseCsv(text);
      if (!headers.length) throw new Error('This file appears to be empty');
      if (rows.length === 0) throw new Error('This file has headers but no data');
      setRawHeaders(headers);
      setRawRows(rows);
      setStep('analyzing');

      // AI mapping
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch('/api/ai-csv-mapper', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ headers, sampleRows: rows.slice(0, 5), target: 'contacts' }),
        });
        if (resp.ok) {
          const aiResult = await resp.json();
          setAiMapping(aiResult);
          const guess = {};
          Object.entries(aiResult.mappings || {}).forEach(([idx, info]) => {
            const field = typeof info === 'string' ? info : info?.field;
            if (field) guess[idx] = field;
          });
          setMapping(guess);
          // If we have required fields, go straight to preview
          const usedFields = Object.values(guess);
          const ready = (usedFields.includes('first_name') && usedFields.includes('last_name')) || usedFields.includes('full_name');
          setStep(ready ? 'preview' : 'needs_help');
        } else {
          fallbackGuess(headers);
        }
      } catch (aiErr) {
        fallbackGuess(headers);
      }
    } catch (e) {
      setError(e.message);
      setStep('upload');
    }
  };

  const fallbackGuess = (headers) => {
    const guess = {};
    headers.forEach((h, i) => { const m = guessField(h); if (m) guess[i] = m; });
    setMapping(guess);
    const used = Object.values(guess);
    const ready = (used.includes('first_name') && used.includes('last_name')) || used.includes('full_name');
    setStep(ready ? 'preview' : 'needs_help');
  };

  const handleImport = async () => {
    setStep('importing');
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch('/api/import-contacts', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: mappedRows, dedupe, default_role: null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Import failed');
      setResult(data);
      setStep('done');
      onImported?.(data);
    } catch (e) {
      setError(e.message);
      setStep('preview');
    }
  };

  const reset = () => {
    setStep('upload'); setRawHeaders([]); setRawRows([]); setMapping({});
    setResult(null); setError(null); setAiMapping(null); setFileName('');
    setCameFromHelp(false);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.colors.card, borderRadius: 16, width: '100%', maxWidth: 640,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${theme.colors.border}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Upload size={18} color={theme.colors.primary} />
          <h3 style={{ flex: 1, margin: 0, fontSize: 17, color: theme.colors.text }}>Import Contacts</h3>
          <button onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* ── Upload ── */}
          {step === 'upload' && (
            <div>
              <label
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: 48, border: `2px dashed ${theme.colors.border}`, borderRadius: 12,
                  cursor: 'pointer', gap: 10, transition: 'border-color 0.2s',
                }}
              >
                <Upload size={36} color={theme.colors.textLight} />
                <div style={{ fontWeight: 600, fontSize: 15, color: theme.colors.text }}>
                  Drop your CSV here or click to browse
                </div>
                <div style={{ fontSize: 12, color: theme.colors.textLight }}>
                  Works with exports from any CRM, spreadsheet, or MLS
                </div>
                <input type="file" accept=".csv,text/csv" onChange={e => handleFile(e.target.files?.[0])} style={{ display: 'none' }} />
              </label>
              {error && <div style={errorBoxStyle}><AlertCircle size={14} /> {error}</div>}
            </div>
          )}

          {/* ── AI Analyzing ── */}
          {step === 'analyzing' && (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Sparkles size={36} color={theme.colors.primary} style={{ animation: 'pulse 1.5s ease-in-out infinite', marginBottom: 16 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text }}>
                Reading your file...
              </div>
              <div style={{ fontSize: 13, color: theme.colors.textLight, marginTop: 6 }}>
                Detecting names, phones, emails, and addresses automatically
              </div>
              <style>{`@keyframes pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.1); } }`}</style>
            </div>
          )}

          {/* ── Preview (main screen) ── */}
          {step === 'preview' && (
            <div>
              {/* Summary bar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
                padding: 16, borderRadius: 10, background: '#F0FDF4', border: '1px solid #BBF7D0',
              }}>
                <CheckCircle2 size={24} color="#16A34A" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#166534' }}>
                    Ready to import {rawRows.length.toLocaleString()} contact{rawRows.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#15803D', marginTop: 2 }}>
                    {mappedCount} fields detected from {fileName}
                    {aiMapping && ' — mapped by AI'}
                  </div>
                </div>
              </div>

              {/* Clean preview table — just the key fields */}
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.textLight, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Sample preview
              </div>
              <div style={{ overflowX: 'auto', border: `1px solid ${theme.colors.border}`, borderRadius: 10, marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: theme.colors.bg }}>
                      {PREVIEW_COLS.filter(c => mappedRows[0]?.[c] !== undefined).map(c => (
                        <th key={c} style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: theme.colors.text, borderBottom: `1px solid ${theme.colors.border}` }}>
                          {PREVIEW_LABELS[c]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mappedRows.slice(0, 5).map((r, i) => (
                      <tr key={i}>
                        {PREVIEW_COLS.filter(c => mappedRows[0]?.[c] !== undefined).map(c => (
                          <td key={c} style={{
                            padding: '8px 12px', borderBottom: `1px solid ${theme.colors.border}40`,
                            color: theme.colors.text, maxWidth: 180, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {r[c] || <span style={{ color: theme.colors.textLight }}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rawRows.length > 5 && (
                <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 16 }}>
                  ...and {(rawRows.length - 5).toLocaleString()} more
                </div>
              )}

              {error && <div style={errorBoxStyle}><AlertCircle size={14} /> {error}</div>}

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <button onClick={() => setStep('mapping')} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '8px 12px', borderRadius: 8, border: 'none',
                  background: 'transparent', color: theme.colors.textLight,
                  fontSize: 12, cursor: 'pointer',
                }}>
                  <Settings2 size={13} /> Adjust columns
                </button>
                <div style={{ flex: 1 }} />
                <button onClick={reset} style={btnGhost}>
                  Cancel
                </button>
                <button onClick={handleImport} style={{
                  ...btnPrimary, padding: '12px 28px', fontSize: 14,
                }}>
                  Import {rawRows.length.toLocaleString()} contacts
                </button>
              </div>
            </div>
          )}

          {/* ── Importing ── */}
          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Loader2 size={36} color={theme.colors.primary} style={{ animation: 'spin 1s linear infinite', marginBottom: 16 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text }}>
                Importing {rawRows.length.toLocaleString()} contacts...
              </div>
              <div style={{ fontSize: 13, color: theme.colors.textLight, marginTop: 6 }}>
                Normalizing phone numbers, checking duplicates
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* ── Done — success + next steps ── */}
          {step === 'done' && result && (
            <div style={{ padding: 24 }}>
              {/* Success header */}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <CheckCircle2 size={48} color="#16A34A" style={{ marginBottom: 12 }} />
                <h3 style={{ margin: '0 0 6px', fontSize: 20, color: theme.colors.text }}>
                  {result.inserted} contact{result.inserted !== 1 ? 's' : ''} imported!
                </h3>
                {result.skipped_dupes > 0 && (
                  <div style={{ color: theme.colors.textLight, fontSize: 13 }}>
                    {result.skipped_dupes} duplicate{result.skipped_dupes !== 1 ? 's' : ''} skipped (already in Trovva)
                  </div>
                )}
                {result.routed > 0 && (
                  <div style={{ color: '#16A34A', fontSize: 13, marginTop: 4 }}>
                    {result.routed} auto-assigned to agents via routing rules
                  </div>
                )}
                {result.validation_errors?.length > 0 && (
                  <details style={{ marginTop: 12, textAlign: 'left' }}>
                    <summary style={{ cursor: 'pointer', color: theme.colors.warning, fontWeight: 600, fontSize: 13 }}>
                      {result.validation_errors.length} row{result.validation_errors.length !== 1 ? 's' : ''} had issues
                    </summary>
                    <div style={{ marginTop: 6, fontSize: 12, color: theme.colors.textLight, maxHeight: 150, overflowY: 'auto' }}>
                      {result.validation_errors.slice(0, 20).map((er, i) => (
                        <div key={i} style={{ padding: '3px 0' }}>Row {er.row}: {er.reason}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>

              {/* Where are my contacts? */}
              <div style={{
                background: `${theme.colors.primary}06`, border: `1px solid ${theme.colors.primary}20`,
                borderRadius: 10, padding: 16, marginBottom: 16,
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text, marginBottom: 8 }}>
                  Where are my contacts?
                </div>
                <div style={{ fontSize: 13, color: theme.colors.textLight, lineHeight: 1.6 }}>
                  Your imported contacts are now on the <strong style={{ color: theme.colors.text }}>My Board</strong> tab
                  with a status of <strong style={{ color: theme.colors.text }}>New</strong>. You can view, search,
                  and manage them from there.
                </div>
              </div>

              {/* What to do next */}
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text, marginBottom: 10 }}>
                What to do next
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {[
                  {
                    icon: '👁️', title: 'Review your contacts',
                    desc: 'Go to My Board to review imported contacts, fix any missing info, and add tags.',
                    action: () => { onClose(); onNavigateTab?.('people'); },
                    btnLabel: 'Go to My Board',
                  },
                  {
                    icon: '👥', title: 'Assign to agents',
                    desc: 'Use Routing rules to auto-distribute leads, or assign manually from the board.',
                    action: () => { onClose(); onNavigateTab?.('routing'); },
                    btnLabel: 'Set up Routing',
                  },
                  {
                    icon: '📧', title: 'Start outreach automatically',
                    desc: 'Create an Action Plan to auto-send welcome emails or follow-up sequences.',
                    action: () => { onClose(); onNavigateTab?.('action_plans'); },
                    btnLabel: 'Create Action Plan',
                  },
                ].map((step, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
                    borderRadius: 8, border: `1px solid ${theme.colors.border}`,
                    background: theme.colors.card,
                  }}>
                    <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>{step.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text, marginBottom: 2 }}>{step.title}</div>
                      <div style={{ fontSize: 12, color: theme.colors.textLight, lineHeight: 1.5 }}>{step.desc}</div>
                    </div>
                    <button onClick={step.action} style={{
                      padding: '6px 12px', borderRadius: 6, border: `1px solid ${theme.colors.primary}30`,
                      background: `${theme.colors.primary}08`, color: theme.colors.primary,
                      fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    }}>{step.btnLabel}</button>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={reset} style={btnGhost}>Import another</button>
                <button onClick={() => { onClose(); onNavigateTab?.('people'); }} style={btnPrimary}>View My Board</button>
              </div>
            </div>
          )}

          {/* ── Needs Help — AI couldn't find required fields ── */}
          {step === 'needs_help' && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: 20, borderRadius: 10,
                background: '#FFFBEB', border: '1px solid #FDE68A', marginBottom: 20,
              }}>
                <AlertCircle size={24} color="#D97706" style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>
                    We need a little help with your file
                  </div>
                  <div style={{ fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
                    Trovva couldn't automatically detect which columns contain <strong>contact names</strong>.
                    Every contact needs at least a name to be imported. Your file has {rawHeaders.length} columns
                    and {rawRows.length.toLocaleString()} rows — we just need you to tell us which column is which.
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 13, color: theme.colors.textLight, marginBottom: 6 }}>
                Here are your CSV column headers:
              </div>
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6, padding: 12, borderRadius: 8,
                background: theme.colors.bg, border: `1px solid ${theme.colors.border}`, marginBottom: 20,
              }}>
                {rawHeaders.map((h, i) => (
                  <span key={i} style={{
                    padding: '4px 10px', borderRadius: 6, background: theme.colors.card,
                    border: `1px solid ${theme.colors.border}`, fontSize: 12, color: theme.colors.text,
                  }}>{h || `Column ${i + 1}`}</span>
                ))}
              </div>

              <button onClick={() => { setCameFromHelp(true); setStep('mapping'); }} style={{
                ...btnPrimary, width: '100%', justifyContent: 'center', padding: '14px 20px', fontSize: 14,
              }}>
                <Settings2 size={16} /> Fix column mapping
              </button>

              <button onClick={reset} style={{
                ...btnGhost, width: '100%', justifyContent: 'center', marginTop: 10,
              }}>
                Try a different file
              </button>
            </div>
          )}

          {/* ── Manual mapping (hidden by default) ── */}
          {step === 'mapping' && (
            <div>
              {/* Detailed instructions */}
              <div style={{
                padding: 14, borderRadius: 10, marginBottom: 16,
                background: '#EFF6FF', border: '1px solid #BFDBFE',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <HelpCircle size={14} color="#2563EB" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1E40AF' }}>How this works</span>
                </div>
                <div style={{ fontSize: 12, color: '#1E40AF', lineHeight: 1.6 }}>
                  Each row below shows one of your CSV columns on the left. Use the dropdown on the right
                  to tell Trovva what that column contains. {cameFromHelp ? (
                    <>You <strong>must</strong> assign at least <strong>First name + Last name</strong>, or
                    a single <strong>Full name</strong> column.</>
                  ) : (
                    <>Set any column to "— ignore —" if you don't need it.</>
                  )} Trovva will automatically format phone numbers, addresses, and dates during import.
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
                {rawHeaders.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 8,
                      background: theme.colors.bg, fontSize: 12, color: theme.colors.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{h || `Column ${i + 1}`}</div>
                    <span style={{ color: theme.colors.textLight, fontSize: 11 }}>→</span>
                    <select
                      value={mapping[i] || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setMapping(m => ({ ...m, [i]: val }));
                        if (aiMapping) {
                          setAiMapping(prev => ({
                            ...prev,
                            mappings: { ...prev.mappings, [i]: { field: val, confidence: 1 } },
                          }));
                        }
                      }}
                      style={{
                        flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 8,
                        border: `1px solid ${theme.colors.border}`, fontSize: 12,
                      }}
                    >
                      {TROVVA_FIELDS.map(f => (
                        <option key={f.key} value={f.key}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {!hasRequired && (
                <div style={{
                  marginTop: 12, padding: 12, background: '#FEF2F2', color: '#991B1B',
                  borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8,
                  border: '1px solid #FECACA',
                }}>
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong>Required:</strong> Assign at least a <strong>First name</strong> + <strong>Last name</strong> column,
                    or a single <strong>Full name</strong> column. Look through your CSV columns above and use
                    the dropdowns to match them.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button onClick={() => { setCameFromHelp(false); setStep('preview'); }} disabled={!hasRequired} style={btnGhost}>
                  Back to preview
                </button>
                <button onClick={() => { setCameFromHelp(false); setStep('preview'); }} disabled={!hasRequired} style={{
                  ...btnPrimary, opacity: hasRequired ? 1 : 0.5,
                }}>
                  Save & continue
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────

function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\r') { /* skip */ }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else field += c;
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  while (rows.length && rows[rows.length - 1].every(v => !v.trim())) rows.pop();
  if (!rows.length) return { headers: [], rows: [] };
  return { headers: rows[0].map(h => h.trim()), rows: rows.slice(1) };
}

function guessField(header) {
  const h = (header || '').toLowerCase().replace(/[^a-z]/g, '');
  const map = {
    firstname: 'first_name', first: 'first_name', fname: 'first_name',
    lastname: 'last_name', last: 'last_name', lname: 'last_name', surname: 'last_name',
    fullname: 'full_name', name: 'full_name',
    email: 'email', emailaddress: 'email', mail: 'email',
    phone: 'phone', mobile: 'phone', cell: 'phone', phonenumber: 'phone',
    address: 'address', street: 'address', streetaddress: 'address',
    city: 'city', state: 'state', zip: 'zip', zipcode: 'zip',
    role: 'role', leadtype: 'lead_type',
    source: 'lead_source', leadsource: 'lead_source',
    notes: 'notes', tags: 'tags',
  };
  return map[h] || null;
}

// Styles as constants (not re-created each render)
const iconBtn = {
  background: 'transparent', border: `1px solid ${theme.colors.border}`,
  color: theme.colors.text, borderRadius: 8, width: 30, height: 30,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 16px', borderRadius: 10, border: 'none',
  background: theme.colors.primary, color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
};
const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 14px', borderRadius: 10, background: 'transparent',
  border: `1px solid ${theme.colors.border}`, color: theme.colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const errorBoxStyle = {
  marginTop: 12, padding: 10, background: '#FEF2F2', color: theme.colors.danger,
  borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
};
